import {
  ExperienceBookFlowPaymentElementContentModelContentType,
  StayBookFlowPaymentElementContentModelContentType,
  SunbedBookFlowPaymentElementContentModelContentType,
  type ApiBlockItemModel,
  type ApiBlockGridItemModel,
} from '@/services/platform/api/model';

type StepItem = ApiBlockItemModel | ApiBlockGridItemModel;

/**
 * The CMS-authored steps of a flow, as they arrive on `content.properties.steps.items`.
 *
 * Order is whatever the editor dragged them into. That matters: reading a step's properties by
 * array position (`steps[1]`) silently hands the wrong content to the wrong screen the moment a
 * block is inserted or reordered, which is exactly what adding a payment step does. Everything
 * below looks steps up by content type instead.
 */
export type FlowSteps = StepItem[] | undefined;

/** The properties of the first step with this content type, or `undefined` if none is authored. */
export function findStepProps<T>(steps: FlowSteps, contentType: string): T | undefined {
  const match = (steps ?? []).find(step => step.content?.contentType === contentType);
  return match?.content?.properties as T | undefined;
}

/** Whether the editor authored a step of this content type at all. */
export function hasStep(steps: FlowSteps, contentType: string): boolean {
  return (steps ?? []).some(step => step.content?.contentType === contentType);
}

/**
 * Position of the first step with this content type, or `-1` when none is authored.
 *
 * The counterpart to `findStepProps` for a flow that still drives its render off a step index. The
 * index has to be *derived*: a constant points at whatever block an editor happens to have inserted
 * before it, which is how a paid event booking ended up on a blank screen — every path said
 * `setStep(2)` and index 2 had become the newly authored payment step.
 */
export function findStepIndex(steps: FlowSteps, contentType: string): number {
  return (steps ?? []).findIndex(step => step.content?.contentType === contentType);
}

/**
 * The payment step element type of each Mews-backed flow.
 *
 * Authoring one of these turns that flow's payment into an inline step; leaving it out keeps the
 * flow on the older path of navigating out to the `/checkout` route and back. That is what lets the
 * two coexist while the CMS is rolled out environment by environment.
 *
 * Gathered here rather than referenced from each flow so the three stay recognisable as one
 * mechanism; the generated enums are the source of truth for the aliases themselves.
 */
export const PaymentStepContentTypes = {
  Sunbed: SunbedBookFlowPaymentElementContentModelContentType.sunbedBookFlowPayment,
  Stay: StayBookFlowPaymentElementContentModelContentType.stayBookFlowPayment,
  Experience: ExperienceBookFlowPaymentElementContentModelContentType.experienceBookFlowPayment,
} as const;
